"use client";

import { useMemo, useState } from "react";

import { fmtIntCA } from "@/lib/format";

import {
  type BlocsView,
  type MatrixView,
  type Voter,
  voterMap,
} from "@/app/council/_lib/alignment-data";

import { blocColor } from "./colors";

type Props = {
  matrix: MatrixView;
  blocs: BlocsView;
  initialK?: 2 | 3 | 4;
};

/**
 * Bloc summary panel. Shows the k=2/3/4 cluster cuts as ink-on-color
 * plates with member chips, plus the within-bloc vs cross-bloc mean
 * agreement (a defensible single-number measure of how meaningful the
 * partition is). Cluster labels stay A/B/C/D — the constitution
 * forbids ideological labelling here; the reader infers identity from
 * the member chips.
 */
export function BlocPanel({ matrix, blocs, initialK = 3 }: Props) {
  const [k, setK] = useState<2 | 3 | 4>(initialK);
  const byId = useMemo(() => voterMap(matrix), [matrix]);
  const clusters =
    k === 2 ? blocs.cuts.k2 : k === 3 ? blocs.cuts.k3 : blocs.cuts.k4;

  const cohesion = useMemo(() => {
    const assignment = new Map<number, string>();
    for (const c of clusters) for (const id of c.member_ids) assignment.set(id, c.label);
    let withinSum = 0;
    let withinN = 0;
    let crossSum = 0;
    let crossN = 0;
    for (const p of matrix.pairs) {
      if (p.agreement_rate === null) continue;
      const la = assignment.get(p.a_id);
      const lb = assignment.get(p.b_id);
      if (!la || !lb) continue;
      if (la === lb) {
        withinSum += p.agreement_rate;
        withinN += 1;
      } else {
        crossSum += p.agreement_rate;
        crossN += 1;
      }
    }
    return {
      within: withinN > 0 ? withinSum / withinN : null,
      cross: crossN > 0 ? crossSum / crossN : null,
      withinN,
      crossN,
    };
  }, [clusters, matrix.pairs]);

  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
        <div>
          <h3 className="cs-display text-xl sm:text-2xl">Voting blocs</h3>
          <p className="text-sm text-ink-3 mt-1">
            Councillors grouped by how often they vote together. Group
            labels (A/B/C/D) are deliberately neutral — read the names
            and decide for yourself what each group has in common.
          </p>
        </div>
        <fieldset
          aria-label="Number of groups to display"
          className="flex items-center gap-1 cs-mono text-xs"
        >
          <span className="text-ink-3 mr-1">groups</span>
          {[2, 3, 4].map((opt) => {
            const active = opt === k;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => setK(opt as 2 | 3 | 4)}
                aria-pressed={active}
                className={
                  "px-2 py-1 border " +
                  (active
                    ? "border-ink bg-ink text-bg"
                    : "border-rule text-ink-2 hover:border-ink-3")
                }
              >
                {opt}
              </button>
            );
          })}
        </fieldset>
      </div>

      <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
        {clusters.map((c) => (
          <article
            key={c.label}
            className="border border-rule px-3 py-3"
            style={{ borderLeftWidth: 6, borderLeftColor: blocColor(c.label) }}
          >
            <header className="flex items-baseline justify-between gap-2">
              <span
                className="cs-num text-2xl leading-none"
                style={{ color: blocColor(c.label) }}
              >
                {c.label}
              </span>
              <span className="cs-mono text-xs text-ink-3">
                {fmtIntCA(c.size)} member{c.size === 1 ? "" : "s"}
              </span>
            </header>
            <ul className="mt-2 flex flex-col gap-0.5 text-sm leading-snug">
              {c.member_ids.map((id) => {
                const v = byId.get(id);
                if (!v) return null;
                return (
                  <li key={id} className="text-ink-2">
                    <Member v={v} />
                  </li>
                );
              })}
            </ul>
          </article>
        ))}
      </div>

      <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Stat
          label="agreement within groups"
          value={cohesion.within}
          n={cohesion.withinN}
          tone="moss"
          hint="How often councillors in the same group vote the same way, on average."
        />
        <Stat
          label="agreement between groups"
          value={cohesion.cross}
          n={cohesion.crossN}
          tone="brick"
          hint="How often councillors in different groups vote the same way, on average. The bigger the gap between this number and the one beside it, the more clearly the groups are separated."
        />
      </dl>

      <div className="mt-4 text-right cs-mono text-[11px] text-ink-3 tracking-wide">
        calgarylens.ca
      </div>
    </div>
  );
}

function Member({ v }: { v: Voter }) {
  if (v.role === "mayor") return <span><span className="cs-mono text-ink-3">mayor · </span>{v.display_name}</span>;
  if (v.ward_num != null)
    return (
      <span>
        <span className="cs-mono text-ink-3">W{v.ward_num} · </span>
        {v.display_name.replace(/\s*\(W\d+\)\s*$/, "")}
      </span>
    );
  return <span>{v.display_name}</span>;
}

function Stat({
  label,
  value,
  n,
  tone,
  hint,
}: {
  label: string;
  value: number | null;
  n: number;
  tone: "moss" | "brick";
  hint: string;
}) {
  return (
    <div
      className="border-l-4 pl-3 py-1"
      style={{ borderColor: tone === "moss" ? "var(--moss)" : "var(--brick)" }}
    >
      <dt className="cs-label">{label}</dt>
      <dd className="mt-0.5 flex items-baseline gap-2">
        <span className="cs-num text-3xl" style={{ fontVariantNumeric: "tabular-nums" }}>
          {value === null ? "—" : `${Math.round(value * 100)}%`}
        </span>
        <span className="cs-mono text-xs text-ink-3">
          n={fmtIntCA(n)}
        </span>
      </dd>
      <p className="mt-1 text-xs text-ink-3 leading-snug">{hint}</p>
    </div>
  );
}
